import { Handle, Position, NodeProps } from '@xyflow/react';
import { memo, Fragment } from 'react';
import ShapeRenderer from './shape.renderer';
import { getHandleStyle } from '../node.utils';
import ShapePopover from './shape.popover';

import { shapeMap } from './shape.map';
import { useDiagramStore } from '@diagram/ui/hooks/diagram-store.hook';
import NodeLabel from '../node.label';
import { NodeId, NodeModel, ShapeNodeModel } from '@diagram/model/node.model';
import useModifierKeyPressed from '../../../../../shared/hooks/useModifierKeyPressed';
import { NodeResizeControl } from '../node.resizer';

const ShapeNode: React.FC<NodeProps<NodeModel>> = memo(
  ({ id, data, selected, width, height }: NodeProps<NodeModel>) => {
    const { shape, label, color } = data as ShapeNodeModel['data'];
    const isModifierPressed = useModifierKeyPressed();
    const config = shapeMap[shape as keyof typeof shapeMap];
    const strokeColor = selected ? color : 'gray';
    const updateNodeLabel = useDiagramStore((s) => s.updateNodeLabel);

    const handleInput = (e: string) => {
      updateNodeLabel(id as NodeId, e);
    };

    return (
      <div className="relative group" style={{ width: width, height: height }}>
        {/* Render shape */}
        <ShapeRenderer
          width={width}
          height={height}
          strokeColor={strokeColor}
          render={config.render}
        />

        {/* Label */}
        <div className="absolute inset-0 flex items-center justify-center w-full h-full px-2 py-1">
          <NodeLabel value={label} onChange={handleInput} />
        </div>

        {Object.values(Position).map((position) => {
          return (
            <Fragment key={position}>
              <ShapePopover position={position} nodeId={id} />
              <Handle
                key={position}
                type="source"
                className="group"
                position={position}
                id={position}
                isConnectable={true}
                style={getHandleStyle(
                  shape as keyof typeof shapeMap,
                  position as Position
                )}
              />
            </Fragment>
          );
        })}

        {/* Handles */}
        <NodeResizeControl
          nodeId={id}
          type="shape"
          show={selected && !isModifierPressed}
          color={strokeColor}
        />
      </div>
    );
  }
);

ShapeNode.displayName = 'ShapeNode';

export default ShapeNode;
